import { Component, ElementRef, OnDestroy, Renderer2, ViewChildren, ViewChild, QueryList, ChangeDetectorRef, OnInit, Input, ChangeDetectionStrategy, OnChanges } from "@angular/core";
import { NavigationService } from "../services/navigation.service";
import { EventsService } from "../../services/events.service";
import { SetFocusDirective } from '../../common/directives/set-focus.directive';
import { NavigationItemType } from "../models/item.model";
import { ActivatedRoute } from "@angular/router";
import { AuthService } from "../../services/auth.service";
import { Subscription } from "rxjs";
import { tap } from "rxjs/operators";
import { NavigationRoot } from "../models/navigation-root.model";
import { Panel } from "../models/panel.model";

export enum MenuItemArray {
    secondary,
    more
}

@Component({
    selector: "nav-menu",
    templateUrl: "./nav-menu.component.html",
    standalone: false
})
export class NavMenuComponent implements OnInit, OnDestroy {
    @ViewChildren(SetFocusDirective) focusable: QueryList<SetFocusDirective>;

    megaMenuBlock: boolean = false;
    data: Panel[] = [];
    show: boolean = false;
    _aryMainButtons: Array<any>;
    _aryMainButtonWidths: Array<any> = [];
    _ndListMoreMenuButtons: NodeList;
    _mobileButton: HTMLButtonElement;
    _availableWidth: number;
    _totalMainNavButtonWidth: number = 0;
    _moreButtonWidth: number = 0;
    showMoreButton: boolean = true; /* display on init for width calculation */
    showMoreMenu: boolean = false;
    _moreMenu: HTMLDivElement;
    _menuWaitTimeout: any;
    _primaryLinkArray: Array<any> = [];         /* array of main menu button that display sub-menus */
    _primaryLinkArrayIndex: number = 0;         /* index of for currently focused main button, displaying sub-menu */
    _activeSecondaryLinkArray: Array<any> = []; /* array of visible secondary (sub-menu) links */
    _activeSecondaryLinkArrayIndex: number = 0; /* index of currently focused secondary link */
    _moreLinkArray: Array<any> = [];            /* array of buttons in more menu */
    _moreLinkIndex: number = 0;                 /* index of currently focused more sub-menu buttons */
    _isActivePrimarySubMenu: boolean = false;
    itemType = NavigationItemType;
    _subscription = new Subscription();   

    _showMenu: boolean = false;
    get showMenu(): boolean {
        return this._showMenu;
    }
 

    constructor(
        public el: ElementRef,
        public _changeDetector: ChangeDetectorRef,
        public _eventsService: EventsService,
        public _activatedRoute: ActivatedRoute,
        public _navService: NavigationService,
        public _authService: AuthService) {
        
    }    

    ngOnInit() {    
        this.updateNavigation();
        let orgs$ = this._authService.selectedOrganization$.pipe().subscribe(
            (org) => {
                if (org && org.length > 0) {
                    this._showMenu = true;
                    this.updateNavigation();
                }
            },
            (error) => {
                console.error(error);
            }
        );
        this._subscription.add(orgs$);
        let nav$ = this._navService.navigation.pipe(tap((menu: NavigationRoot) => {
            if (menu) {
                this.data = menu.panels;
            } else {
                this.data = [];
            }
            this._showMenu = this._authService.User.isLoggedIn;
        })).subscribe(
            () => {
                if (this.data) {
                    for (let d of this.data) {
                        d.show = false;
                        d.expanded = false;
                    }
                }
                if (this.showMenu) {
                    this.updateMegaMenu();
                }
            },
            (error) => { console.error(error); }
        );
        this._subscription.add(nav$);
    }

    ngOnDestroy() {
        this._eventsService.remove('nav');
        this._subscription.unsubscribe();
    }    

    ngAfterViewInit() {
        //create subscription and call checknav on window resize
        this._eventsService.create('nav', window, 'resize', () => {
            this._aryMainButtons = Array.from(this.el.nativeElement.querySelectorAll('.mega-menu-dropdown-icon'));
            this._mobileButton = this.el.nativeElement.querySelector(".mega-menu-mobile");
            this._moreMenu = this.el.nativeElement.querySelector(".more-menu");
            this.checkNavWidth();
        });
    }

    updateMegaMenu(): void {
        //only top buttons, not more-menu buttons
        this._aryMainButtons = Array.from(this.el.nativeElement.querySelectorAll('.mega-menu-dropdown-icon'));
        this._mobileButton = this.el.nativeElement.querySelector(".mega-menu-mobile");

        this._moreMenu = this.el.nativeElement.querySelector(".more-menu");

        //hide/show 'More...' button on init
        this.checkNavWidth();
        this._changeDetector.detectChanges();

      
    }

    updateNavigation(): void {
        let obs$ = this._authService.generateNavigation().pipe(tap((menu: NavigationRoot) => {
            if (menu) {
                this.data = menu.panels;
            } else {
                this.data = [];
            }
        })).subscribe(
            () => {
                if (this.data) {
                    for (let d of this.data) {
                        d.show = false;
                        d.expanded = false;
                    }
                }
                if (this.showMenu) {
                    this.updateMegaMenu();
                }                
            },
            (error) => { console.error(error); });
        this._subscription.add(obs$);  
    }


    /**
    * Add 'More..' link if there isn't enough space for main links
    */
    checkNavWidth(): void {

        this._ndListMoreMenuButtons = this._moreMenu.querySelectorAll('.mega-menu-option'); 
        this._availableWidth = this.el.nativeElement.querySelector("nav").clientWidth;

        //not in mobile view?
        if (!this.isDisplayingMobileButton()) {
            if (this._totalMainNavButtonWidth == 0) this.setTotalButtonWidth();
            if (this._moreButtonWidth == 0) this.setMoreButtonWidth();
            //hide/show more button
            let calculated : boolean = this._totalMainNavButtonWidth > (this._availableWidth - this._moreButtonWidth) ? true : false;
            this.showMoreButton = calculated;
            //hide/show extra main links
            this.handleHiddenMainNavButtons();
        }
    }

    /**
    * Are we in mobile nav mode (css based)?
    */
    isDisplayingMobileButton(): boolean {
        return getComputedStyle(this._mobileButton).display == 'block';
    }

    /**
    * Hide/show navigation buttons
    */
    handleHiddenMainNavButtons(): void {
        this._aryMainButtonWidths.forEach((btnObject, index) => {
            let moreButtonMenuItem = <HTMLButtonElement>this._ndListMoreMenuButtons.item(index);

            //does this button have enough room to display horizontally?
            if (btnObject.widthToButton > (this._availableWidth - this._moreButtonWidth)) {
                //hide main nav button
                btnObject.button.classList.add('hideBtn');
                //show 'show more' menu item
                moreButtonMenuItem.classList.remove("hideBtn");
            } else {
                btnObject.button.classList.remove('hideBtn');
                moreButtonMenuItem.classList.add("hideBtn");
            }
        });
    }

    /**
    * Width of 'More...' button
    */
    setMoreButtonWidth(): void {
        let moreButton = <HTMLButtonElement>this.el.nativeElement.querySelector(".more-link");
        let moreButtonWidth = Math.ceil(moreButton.offsetWidth) + parseInt(getComputedStyle(moreButton).marginLeft);
        //invisible property allows for width calc while hidden, no longer needed
        moreButton.classList.remove("invisible");
        this._moreButtonWidth = moreButtonWidth;
    }

    /**
     * Width of main nav buttons, including margin-left
     */
    setTotalButtonWidth() {
        let totalButtonWidth: number = 0;

        this._aryMainButtons.forEach((button) => {
            //get cumulative button width
            totalButtonWidth += Math.ceil(button.offsetWidth) + parseInt(getComputedStyle(button).marginLeft);
            //build array of buttons & cumulative widths
            this._aryMainButtonWidths.push({ widthToButton: totalButtonWidth, button: button })
        });

        this._totalMainNavButtonWidth = totalButtonWidth;
    }

    /**
     * Sets proper menu tabindex for keyboard users
     * @param navItem Navigation Item
     */
    setNavTabIndex(navItem: any): void {
        let ndLst = this.el.nativeElement.querySelectorAll(".mega-menu-dropdown-icon.hideBtn");
        let targetButton: HTMLButtonElement;

        //find matching hidden main link
        ndLst.forEach(node => {
            if (node.textContent.trim() == navItem.title.trim()) {
                targetButton = <HTMLButtonElement>node;
            }
        });

        //get the associated menu
        let ulMgaMenuBlock = <HTMLUListElement>targetButton.nextElementSibling;

        //set focus to displaying menu
        setTimeout(() =>
            ulMgaMenuBlock.focus()
            , 1);

        //hide more menu
        this.showMoreMenu = false;
    }

    /**
     * Toggles 'More...' menu if using keyboard
     * @param navItem Navigation Item
     * @param visible Boolean
     * @param event MouseEvent
     */
    moreToggleClick(navItem: any, visible: boolean, event: MouseEvent): void {
        //is it a keyboard event?
        if (event.clientX == 0) {
            //allow proper toggle
            this.toggleItemVisible(navItem, visible);
            if (visible) {
                //set tab to opened sub menu
                this.setNavTabIndex(navItem);
            }
        }
    }

    /**
     * Toggles Visiblity for a given Navigation node
     * @param navItem Navigation Item
     * @param visible boolean
     */
    toggleItemVisible(navItem?: any, visible: boolean = false): void {
        //block hiding menu if user re-enters the menu during the hiding wait time (508 for inacurate mouse movements)
        clearTimeout(this._menuWaitTimeout);

        if (!this.data) return;

        //hide previously open menu
        this.hideOpenMenuItems();

        //set visibility
        if (navItem) {
            navItem.show = visible;
            navItem.expanded = visible;
        }
    }

    /**
     * Hides menu on mouseout (all Navigation nodes)
     *  508 - Wait short period before hiding menu - support for inacurate mouse movements
     */
    hideMenu() {
        //catch for mutliple calls within wait period
        clearTimeout(this._menuWaitTimeout);

        //wait before hiding the menu and setting all menu items to hidden
        this._menuWaitTimeout = setTimeout((e: Event) => {
            this.megaMenuBlock = false;
            this.hideOpenMenuItems();
        }, 500);
    }

    /**
     * Hide currently visible menu items 
     */
    public hideOpenMenuItems() {
        if (!this.data) return;

        let shownItemIndex = this.data.findIndex(x => x.show);

        //clear previously shown menus
        if (shownItemIndex > -1) {
            this.data[shownItemIndex].show = false;
            this.data[shownItemIndex].expanded = false;
        }
    }

    /**
     * @param e - FocusEvent (from tabbing)
     */
    navBlur(e: FocusEvent) {
        let relateTargetElement = <HTMLElement>e.relatedTarget;

        //hide if we are moving to an element that isn't in the menu
        if (relateTargetElement && relateTargetElement.getAttribute("role") !== "menuitem") {
            this.hideMenu();
        }
    }

    /**
     * "More..." mouse events
     * @param e
     */
    moreMouseEvent(e: MouseEvent): void {
        if (e.type == "mouseleave")
            this.showMoreMenu = false;
        if (e.type == "mouseover") {
            this.showMoreMenu = true;
            this.hideMenu();
        }
    }

    /**
     * "More..." link key down
     * @param e
     */
    moreKeyDown(e: KeyboardEvent): void {
        //do we have an open event
        if (this.isKeyboardOpenEvent(e)) {
            //hide non-more sub-menu
            this.hideMenu();
            this._isActivePrimarySubMenu = false;

            //Enter key event opens the menu via the 'click' event
            if (this.isNotEnterKeyEvent(e))
                this.showMoreMenu = true;

            //set array of menu items
            this._moreLinkArray = this.focusable.toArray().filter(focus => focus.id.indexOf("primaryLinkMore") != -1 && !<HTMLButtonElement>focus.getElementRef().nativeElement.classList.contains('hideBtn'));
            //set index for first link
            this._moreLinkIndex = 0;
            //focus on the first element
            this.focusElement(this._moreLinkArray, this._moreLinkIndex);

        }
    }

    /**
     * Triggers when a More sub-menu it focused
     * @param navItem
     * @param index
     */
    moreMenuItemFocus(navItem: any, index: number): void {
        //show secondary menu
        this.toggleItemVisible(navItem, true);

        //set values for focuing on secondary 'More...' links (full width)
        this._activeSecondaryLinkArray = this.focusable.toArray().filter(focus => focus.id.indexOf("secondaryLink" + index) != -1);
        this._activeSecondaryLinkArrayIndex = this._activeSecondaryLinkArray.findIndex(focus => focus.id == "secondaryLink" + index + "00");
    }

    /**
     * More menu item key down
     * @param e
     * @param index
     */
    moreMenuitemKeyDown(e: KeyboardEvent, index: number): void {
        //set link index
        this._moreLinkIndex = this._moreLinkArray.findIndex(more => more.id == "primaryLinkMore" + index);

        //focus on next/previous 'More...' sub-menu item
        if (this.isKeyboardMenuItemFocusEvent(e))
            this.moverMenuItemFocus(e, MenuItemArray.more);

        //is it a close menu keyboard event?
        if (this.isKeyboardClosingEvent(e)) {
            //hide more sub-menu secondary items (full width menu of More sub-menu items)
            this.hideMenu();
            //hide more menu (small menu in upper right corner)
            this.showMoreMenu = false;
            //focus on "More..." link
            this.setMoreButtonFocus()
        }

        //is it an event to move form "More..." sub-menu to "More..." sub-menu items (full width)?
        if (this.isKeyboardMoreSubMenuOpenEvent(e)) {
            //focus first menu item
            this.focusElement(this._activeSecondaryLinkArray, this._activeSecondaryLinkArrayIndex);
            //hide more menu (small menu in upper right corner)
            this.showMoreMenu = false;
        }
    }

    /**
     * Main nav links key down
     * @param e
     * @param navItem
     * @param visible
     * @param index
     */
    menubarNavKeyDown(e: KeyboardEvent, navItem: any, visible: boolean, index: number): void {
        //set arrays and indexes
        this._primaryLinkArray = this.focusable.toArray().filter(focus => focus.id.indexOf("primaryLink") != -1);
        this._primaryLinkArrayIndex = this._primaryLinkArray.findIndex(focus => focus.id == "primaryLink" + index);
        this._activeSecondaryLinkArray = this.focusable.toArray().filter(focus => focus.id.indexOf("secondaryLink" + index) != -1);
        this._activeSecondaryLinkArrayIndex = this._activeSecondaryLinkArray.findIndex(focus => focus.id == "secondaryLink" + index + "00");
        this._isActivePrimarySubMenu = true;

        //is it a space, enter or down arrow keyboard event && requesting the menu be visible?
        if (this.isKeyboardOpenEvent(e) && visible) {

            //Enter key also triggers 'click' event, which opens the menu
            if (this.isNotEnterKeyEvent(e))
                this.toggleItemVisible(navItem, visible);

            //focus first menu item
            this.focusElement(this._activeSecondaryLinkArray, this._activeSecondaryLinkArrayIndex);
        }
    }

    /**
     * Sub-menu header (bold text with underline) key down
     * @param e
     * @param secondaryIndex
     * @param primaryIndex
     */
    menuHeadingNavKeyDown(e: KeyboardEvent, secondaryIndex: number, primaryIndex: number): void {
        //set secondary link index
        this._activeSecondaryLinkArrayIndex = this._activeSecondaryLinkArray.findIndex(focus => focus.id == "secondaryLink" + primaryIndex + secondaryIndex + "0");
        //call menu item click
        if (this.isKeyboardMenuItemFocusEvent(e))
            this.moverMenuItemFocus(e, MenuItemArray.secondary);

        //is it a close menu keyboard event?
        if (this.isKeyboardClosingEvent(e))
            this.hideMenuSetPrimaryFocus();
    }

    /**
     * Sub-menu item key down
     * @param e
     * @param id
     */
    menuitemNavKeyDown(e: KeyboardEvent, id: string): void {
        //set the current index for the secondary link array
        this._activeSecondaryLinkArrayIndex = this._activeSecondaryLinkArray.findIndex(focus => focus.id == id);

        // do we have an 'Arrow Up' or 'Arrow Down' keyboard event? 
        if (this.isKeyboardMenuItemFocusEvent(e))
            this.moverMenuItemFocus(e, MenuItemArray.secondary); //set proper menu item focus

        //is it a close menu keyboard event?
        if (this.isKeyboardClosingEvent(e))
            this.hideMenuSetPrimaryFocus();
    }

    /**
     * Sets focus to "More..." link
     */
    public setMoreButtonFocus() {
        let moreButton = <SetFocusDirective>this.focusable.toArray().find(focus => focus.id == "moreLink");
        moreButton.setElementFocus();
    }

    /**
     * Closes open menu and sets focus back to primary button from closed menu
     */
    public hideMenuSetPrimaryFocus(): void {

        if (this._isActivePrimarySubMenu) {
            //closed a non-more menu. Focus primary link (button) for the displayed sub-menu
            this.focusElement(this._primaryLinkArray, this._primaryLinkArrayIndex);
        } else {
            //closed a "More..." sub menu
            this.setMoreButtonFocus();
        }

        //hide open menu
        this.hideMenu();
    }

    /**
     * focus sub-menu items - for up and down arrow looping in sub-menu 
     * @param e
     */
    public moverMenuItemFocus(e, arrayType: MenuItemArray): void {
        let targetArray = [];
        let targetIndex = 0;

        if (arrayType == MenuItemArray.secondary) {
            targetArray = this._activeSecondaryLinkArray;
            targetIndex = this._activeSecondaryLinkArrayIndex;
        }
        if (arrayType == MenuItemArray.more) {
            targetArray = this._moreLinkArray;
            targetIndex = this._moreLinkIndex;
        }

        //focus on next menu item
        if (e.code === "ArrowDown")
            this.focusElement(targetArray, this.newIndexOutOfRange(targetIndex + 1, targetArray));

        //focus on previous menu item
        if (e.code === "ArrowUp")
            this.focusElement(targetArray, this.newIndexOutOfRange(targetIndex - 1, targetArray));
    }

    /**
     * Helper to set focus to any "setFocus" directive
     * @param focusArray
     * @param index
     */
    public focusElement(focusArray: Array<any>, index: number): void {
        //get target element as directive
        let menuItem = <SetFocusDirective>focusArray[index];
        if (menuItem) {
            //focus it
            menuItem.setElementFocus();
        } else {
            console.log("Missed menuItem in nav-menu.component.ts > focusElement");
        }
    }

    /**
     * makes sure sub-menu item looping doesn't go outside array length
     * @param newIndex
     * @param array
     */
    public newIndexOutOfRange(newIndex: number, array: Array<any>): number {
        //too large - start at beginning of list
        if (newIndex > array.length - 1) {
            return 0;
        }
        //too small - start at end of list
        if (newIndex < 0) {
            return array.length - 1;
        }
        //number is good, return it
        return newIndex;
    }

    /**
     * Keyboard key codes that can be used to move between sub-menu items
     * @param e
     */
    public isKeyboardMenuItemFocusEvent(e: KeyboardEvent): boolean {
        switch (e.keyCode) {
            case 40:        //ArrowDown
            case 38:        //ArrowUp
                return true;
            default:
                return false;
        }
    }

    /**
     * Keyboard key codes that can be used to open a "More..." sub-menu
     * @param e
     */
    public isKeyboardMoreSubMenuOpenEvent(e: KeyboardEvent): boolean {

        switch (e.keyCode) {
            case 13:        //Enter
            case 32:        //Space
                return true;
            default:
                return false;
        }
    }

    /**
     * Keyboard key codes that can be used to open a menu
     * @param e
     */
    public isKeyboardOpenEvent(e: KeyboardEvent): boolean {

        switch (e.keyCode) {
            case 40:        //ArrowDown
            case 13:        //Enter
            case 32:        //Space
                return true;
            default:
                return false;
        }
    }

    /**
     * Keyboard key codes that can be used to close an open menu
     * @param e
     */
    public isKeyboardClosingEvent(e: KeyboardEvent): boolean {

        switch (e.keyCode) {
            case 9:         //Tab
            case 27:        //Escape
                return true;
            default:
                return false;
        }
    }

    /**
     * Helper, filter for 'Enter' keyboard event
     * @param e
     */
    public isNotEnterKeyEvent(e: KeyboardEvent): boolean {
        return e.keyCode != 13;
    }

}